import { FC, PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import styled, { DefaultTheme, StyledComponent, css } from 'styled-components';

import { ReactComponent as SortDown } from '@/assets/icons/sort-down.svg';
import { ReactComponent as SortUp } from '@/assets/icons/sort-up.svg';
import { ReactComponent as Sort } from '@/assets/icons/sort.svg';
import Image from '@/components/Image';
import Text, { TextProps } from '@/components/Text';
import { SortState } from '@/types/resources';
import { SortDirection } from '@/utils/enums';
import { RootState } from '@/utils/store';
import { scrollBarStyles } from '@/utils/theme';

import { Spinner } from './Button';

export const ResponsiveTable = styled.div`
  /* TODO: check for solution, because dropdowns is not visible */
  /* -webkit-overflow-scrolling: touch; */
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  ${scrollBarStyles};
`;
const StyledText = styled(Text)`
  margin-left: 20px;
`;

const FilterButton = styled.button`
  font-size: 11px;
  font-weight: 700;
  svg {
    margin-right: 6px;
    margin-bottom: -2px;
    flex-shrink: 0;
  }
`;

export const TableHeader = styled.div`
  display: flex;
  align-items: flex-end;
  margin: 0 0 12px;
  justify-content: space-between;
`;

interface TableProps {
  $fixed?: boolean;
  $stickyActions?: boolean;
}

export const Table = styled.table<TableProps>`
  width: 100%;
  vertical-align: middle;
  text-align: left;
  ${({ $fixed }) =>
    $fixed &&
    css`
      table-layout: fixed;
    `}
  ${({ $stickyActions, theme }) =>
    $stickyActions &&
    css`
      border-collapse: separate;
      td {
        &:last-child {
          position: sticky !important;
          right: 0;
          top: 0;
          bottom: 0;
          z-index: 1;
          border-left: 1px solid ${theme.colors.grayscale} !important;
        }
      }
    `}
  tbody {
    tr:last-child {
      td {
        border: 0;
      }
    }
  }
`;

export const Topics = styled.div<{ $hasTopics: boolean }>`
  display: flex;
  align-items: flex-start;
  > svg {
    width: 14px;
    height: 14px;
    margin-right: 4px;
    flex-shrink: 0;
  }
`;

export const TopicsList = styled.div<{ $hasIcon?: boolean }>`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: ${({ $hasIcon = false }) => ($hasIcon ? '-5px' : '0')};
`;

interface ThProps {
  $actionsCol?: boolean;
}

export const Th = styled(Text).attrs({
  as: 'th',
  $type: 'smallHeadline',
})<ThProps>`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  vertical-align: middle;
  white-space: nowrap;
  text-transform: capitalize;
  background: ${({ theme }) => theme.colors.white};
  ${({ $actionsCol = false }) =>
    $actionsCol &&
    css`
      position: relative;
      width: 56px;
      padding-right: 20px;
      vertical-align: middle !important;
    `}
` as StyledComponent<'th', DefaultTheme, TextProps & ThProps, never>;

interface TdProps {
  $actionsCol?: boolean;
  $centered?: boolean;
}

export const MediumTh = styled(Th)`
  min-width: 120px;
`;

export const LargeTh = styled(Th)`
  min-width: 145px;
`;

export const ExtraLargeTh = styled(Th)`
  min-width: 180px !important;
`;

export const Td = styled(Text).attrs({
  as: 'td',
  $type: 'smallMedium',
})<TdProps>`
  padding: 24px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  vertical-align: middle;
  background: ${({ theme }) => theme.colors.white};
  ${({ $actionsCol }) =>
    $actionsCol &&
    css`
      width: 56px;
      vertical-align: middle !important;
      padding-right: 20px;
      text-align: center;
    `}
  ${({ $centered }) =>
    $centered &&
    css`
      text-align: center;
    `}
` as StyledComponent<'td', DefaultTheme, TextProps & TdProps, never>;

export const ActionsWrap = styled.div`
  z-index: 1;
  position: relative;
`;

export const Thumbnail = styled(Image)`
  width: 100%;
  height: 52px;
`;

const StickyInnerWrap = styled.div<{ $fullScreenModeEnabled: boolean }>`
  max-width: 1120px;
  position: sticky;
  left: 0;

  ${({ $fullScreenModeEnabled }) =>
    $fullScreenModeEnabled &&
    css`
      max-width: calc(100vw - 48px);
    `}
`;

const FullWidthTd = styled(Td)`
  padding-left: 0 !important;
  padding-right: 0 !important;
  text-align: center !important;
`;
const NoResultsText = styled(Text)`
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: center;
  opacity: 0.5;
  padding: 20px 0;
  svg {
    margin-right: 6px;
    transform: translateY(-1px);
  }
`;

export const FullWidthRow: FC<
  PropsWithChildren<{ colSpan?: number; className?: string }>
> = ({ colSpan = 100, children, className }) => {
  const fullScreenModeEnabled = useSelector(
    (rootState: RootState) => rootState.layout.fullScreenModeEnabled,
  );

  return (
    <tr>
      <FullWidthTd className={className} $centered colSpan={colSpan}>
        <StickyInnerWrap $fullScreenModeEnabled={fullScreenModeEnabled}>
          {children}
        </StickyInnerWrap>
      </FullWidthTd>
    </tr>
  );
};

const StyledSpinner = styled(Spinner)`
  width: 18px;
  height: 18px;
  margin-bottom: -5px;
`;

export const SortableTh: FC<
  PropsWithChildren<{
    sort: SortState;
    setSort: React.Dispatch<React.SetStateAction<SortState>>;
    title: string;
    disableDefault?: boolean;
    disableSort?: boolean;
    property: string;
    subtitle?: string;
    className?: string;
    topTitle?: string;
    type?: 'properties' | 'stats' | 'attributes' | 'searchStats';
  }>
> = ({
  setSort,
  title,
  type,
  subtitle,
  sort,
  property,
  children,
  className,
  topTitle,
  disableDefault = false,
  disableSort = false,
}) => {
  const sortIcon = () => {
    if (sort.property !== property) {
      return <Sort />;
    }
    switch (sort.direction) {
      case 'ASC':
        return <SortUp />;
      case 'DESC':
        return <SortDown />;
      default:
        return <Sort />;
    }
  };

  const getSortDirection = (previuosSort: SortState) => {
    if (previuosSort.property !== property) return SortDirection.DESC;

    switch (previuosSort.direction) {
      case SortDirection.DEFAULT:
        return SortDirection.DESC;
      case SortDirection.DESC:
        return SortDirection.ASC;
      default:
        if (disableDefault) {
          return SortDirection.DESC;
        }
        return SortDirection.DEFAULT;
    }
  };

  const onClick = () => {
    setSort((prevSort) => {
      const direction = getSortDirection(prevSort);
      return {
        property,
        direction,
        type,
      };
    });
  };

  if (disableSort) {
    return (
      <Th className={className}>
        {title}
        {!!subtitle && (
          <>
            <br />
            <StyledText as="span" $color="gray" $type="extraSmall">
              {subtitle}
            </StyledText>
          </>
        )}
      </Th>
    );
  }

  return (
    <Th className={className}>
      <FilterButton onClick={onClick}>
        {sortIcon()}
        {!!topTitle && (
          <>
            {topTitle} <br />
          </>
        )}
        {title}
      </FilterButton>
      {children}
      {!!subtitle && (
        <>
          <br />
          <StyledText as="span" $color="gray" $type="extraSmall">
            {subtitle}
          </StyledText>
        </>
      )}
    </Th>
  );
};

export const TableContent: FC<
  PropsWithChildren<{
    colSpan?: number;
    isLoading: boolean;
    isError: boolean;
    hasData: boolean;
    emptyTableText?: string;
  }>
> = ({
  colSpan,
  isLoading,
  isError,
  hasData,
  children,
  emptyTableText = 'No Data',
}) => {
  return isLoading ? (
    <FullWidthRow colSpan={colSpan}>
      <StyledSpinner />
      Loading data...
    </FullWidthRow>
  ) : isError ? (
    <FullWidthRow colSpan={colSpan}>An error occurred!</FullWidthRow>
  ) : hasData ? (
    <>{children}</>
  ) : (
    <FullWidthRow colSpan={colSpan}>
      <NoResultsText $type="caption" $color="gray">
        <svg
          width="31"
          height="31"
          viewBox="0 0 31 31"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.5011 9.76001H26.1083C26.364 9.76001 26.6093 9.86115 26.7901 10.0412C26.971 10.2212 27.0725 10.4654 27.0725 10.72V24.16C27.0725 24.4146 26.971 24.6588 26.7901 24.8388C26.6093 25.0189 26.364 25.12 26.1083 25.12H4.89397C4.63846 25.1193 4.39362 25.0179 4.21295 24.838C4.03227 24.6581 3.93044 24.4144 3.92969 24.16V12.64"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.3225 12.64H3.92969V7.84C3.92969 7.5854 4.03128 7.34122 4.21212 7.16118C4.39296 6.98115 4.63823 6.88 4.89397 6.88H11.3225C11.5312 6.88 11.7342 6.94738 11.9011 7.072L15.5011 9.76L11.9011 12.448C11.7342 12.5726 11.5312 12.64 11.3225 12.64Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {emptyTableText}
      </NoResultsText>
    </FullWidthRow>
  );
};

const StyledShowMore = styled(Text)`
  display: inline-flex;
  align-items: center;
  transition: 0.2s ease-out opacity;
  width: 144px;
  height: 40px;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 50px;
  svg {
    margin-right: 12px;
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const ShowMoreWrap = styled.div`
  text-align: center;
  margin-top: 24px;
`;

export const ShowMoreButton: FC<{
  onClick: () => void;
  loading: boolean;
  text?: string;
}> = ({ onClick, loading = false, text = 'Show more' }) => {
  return (
    <ShowMoreWrap>
      <StyledShowMore
        as="button"
        onClick={onClick}
        $type="button"
        disabled={loading}
        type="button"
      >
        {loading ? (
          <Spinner />
        ) : (
          <svg
            width="11"
            height="8"
            viewBox="0 0 11 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 1.5L5.5 6.5L1 1.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {text}
      </StyledShowMore>
    </ShowMoreWrap>
  );
};
